import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Input,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { customerTimezone, defaultTimezone } from "../../constants";
import { createTimezoneTitle } from "../../notifications/pages/GeneralNotifications/FrequencyNotificationsSection/TimezoneSelector";

const InitiateNewEnvModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();

  const [customerDetails, setCustomerDetails] = useState({
    customerId: "",
    customerEmail: "",
    displayName: "",
    timezone: defaultTimezone,
    showNightlyFlow: false,
  });

  const [onSave, setOnSave] = useState(false);
  const [isAllPropertiesFilled, setIsAllPropertiesFilled] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const setAllProperties = () => {
    setCustomerDetails({
      customerId: "",
      customerEmail: "",
      displayName: "",
      timezone: defaultTimezone,
      showNightlyFlow: false,
    });
    setIsAllPropertiesFilled(false);
  };

  const { initiateNewEnv } = useAPI();

  const handleSave = () => {
    setOnSave(true);
    initiateNewEnv({
      customer_id: customerDetails.customerId,
      customer_email: customerDetails.customerEmail,
      display_name: customerDetails.displayName,
      timezone: customerDetails.timezone,
      show_nightly_flow: customerDetails.showNightlyFlow,
    })
      .then((response) => {
        trackEvent(`Initiated new env for ${customerDetails.customerId}`);
        setShowSuccessModal(true);
      })
      .catch((error) => {
        trackEvent(`Failed to initiate env for ${customerDetails.customerId}`);
        if (error.response) {
          Toast.error(error.response.data);
        } else {
          Toast.error("Failed to initiate new env");
        }
      })
      .finally(() => {
        setOnSave(false);
      });
  };

  const handleCancel = () => {
    trackEvent("User cancelled the initiate new env dialog");
    setAllProperties();
    handleClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    handleCancel();
  };

  useEffect(() => {
    if (
      customerDetails.customerId &&
      customerDetails.customerEmail &&
      customerDetails.displayName &&
      customerDetails.timezone
    ) {
      setIsAllPropertiesFilled(true);
    } else {
      setIsAllPropertiesFilled(false);
    }
  }, [customerDetails]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
      >
        <Box
          sx={{
            width: 500,
            bgcolor: "background.paper",
            margin: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            overflowY: "auto",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: colors.common_white,
                pt: "5%",
              }}
              id="modal-modal-title"
            >
              <Typography variant="subtitle1" gutterBottom>
                Initiate New Env
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ marginTop: "4px" }}
              >
                Enter environment details
              </Typography>
            </Box>
            <Box sx={{ padding: "32px" }}>
              <FormControl fullWidth>
                <Typography sx={{ marginTop: "8px" }}>Customer ID</Typography>
                <Input
                  id="customer-id-input"
                  sx={{ border: 0, outline: 0, minHeight: "40px" }}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      customerId: e.target.value,
                    })
                  }
                />

                <Divider />

                <Typography sx={{ marginTop: "16px" }}>
                  Customer Email
                </Typography>
                <Input
                  id="customer-email-input"
                  sx={{ border: 0, outline: 0, minHeight: "40px" }}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      customerEmail: e.target.value,
                    })
                  }
                />

                <Divider />

                <Typography sx={{ marginTop: "16px" }}>Display Name</Typography>
                <Input
                  id="display-name-input"
                  sx={{ border: 0, outline: 0, minHeight: "40px" }}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      displayName: e.target.value,
                    })
                  }
                />

                <Divider />

                <Typography sx={{ marginTop: "16px" }}>Timezone</Typography>
                <Select
                  labelId="select-timezone"
                  id="timezone-input"
                  value={customerDetails.timezone}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      timezone: e.target.value,
                    })
                  }
                  sx={{
                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                    "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                    minHeight: "40px",
                  }}
                >
                  {Object.keys(customerTimezone).map((ianaTimezone) => (
                    <MenuItem key={ianaTimezone} value={ianaTimezone}>
                      {createTimezoneTitle(ianaTimezone)}
                    </MenuItem>
                  ))}
                </Select>

                <Divider />

                <FormControlLabel
                  sx={{ marginTop: "16px" }}
                  control={
                    <Checkbox
                      checked={customerDetails.showNightlyFlow}
                      onChange={(e) =>
                        setCustomerDetails({
                          ...customerDetails,
                          showNightlyFlow: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Show Nightly Flow"
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px 32px",
                backgroundColor: colors.common_white,
                borderTop: `1px solid ${colors.divider}`,
              }}
            >
              <SecondaryButton
                onClick={handleCancel}
                sx={{
                  color: colors.primary_main,
                  borderRadius: "10px",
                  border: "none",
                  height: "30px",
                  fontSize: "12px",
                }}
              >
                Cancel
              </SecondaryButton>
              {onSave ? (
                <LoadingIcon />
              ) : (
                <PrimaryButton
                  onClick={handleSave}
                  disabled={!isAllPropertiesFilled || onSave}
                  style={{
                    color: colors.common_white,
                    backgroundColor:
                      isAllPropertiesFilled && !onSave
                        ? colors.primary_main
                        : colors.primary_light,
                    borderRadius: "10px",
                    border: "none",
                    cursor:
                      isAllPropertiesFilled && !onSave
                        ? "pointer"
                        : "not-allowed",
                  }}
                  size="small"
                >
                  Initiate
                </PrimaryButton>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        aria-labelledby="success-modal-title"
      >
        <Box
          sx={{
            width: 400,
            bgcolor: "background.paper",
            margin: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            borderRadius: "10px",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" id="success-modal-title" gutterBottom>
            Environment Initiated Successfully
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            The new environment has been created and configured successfully.
          </Typography>
          <PrimaryButton
            onClick={handleSuccessModalClose}
            style={{
              color: colors.common_white,
              backgroundColor: colors.primary_main,
              borderRadius: "10px",
              border: "none",
            }}
          >
            OK
          </PrimaryButton>
        </Box>
      </Modal>
    </>
  );
};

export default InitiateNewEnvModal;
